/**
 * Font data types and helpers. Font data is loaded from the API and stored in Redux.
 */

import { FONT_TYPE_LETTERING, FONT_TYPE_SINGLE_LETTERS } from '../../constants/fontTypes';

export interface FontSizeOption {
  value: string;
  upperCaseSize: number | null;
  lowerCaseSize: number | null;
  numberSizeOnly: boolean;
  /** Execution types available for this size (e.g. "Lettering", "Einzelbuchstaben"). */
  fontTypes?: string[];
}

export interface FontMaterialType {
  identifier: string;
  name: string;
}

export interface FontPatina {
  value: string;
}

export interface CrossingSize {
  height: number;
  width: number;
}

export interface FontDataEntry {
  identifier: string;
  name: string;
  materialTypes: FontMaterialType[];
  sizes: FontSizeOption[];
  individualLetters?: string | null;
  lettering?: string | null;
  upperCases?: boolean;
  lowerCases?: boolean;
  specialCharacters?: string;
  dowelXxl?: boolean;
  patina?: {
    value: string;
  };
  crossingSizes?: CrossingSize[];
  /** Optional character used to render the support cross glyph (e.g. "+"). If omitted, "+" is used. */
  supportCrossCharacter?: string;
}


export function getFontByIdentifier(
  data: FontDataEntry[],
  identifier: string
): FontDataEntry | undefined {
  return data.find((f) => f.identifier === identifier);
}

export function getMaterialForFont(
  data: FontDataEntry[],
  fontIdentifier: string,
  materialIdentifier: string
): FontMaterialType | undefined {
  const font = getFontByIdentifier(data, fontIdentifier);
  return font?.materialTypes?.find((m) => m.identifier === materialIdentifier);
}

export function getSizesForFont(data: FontDataEntry[], fontIdentifier: string): FontSizeOption[] {
  const font = getFontByIdentifier(data, fontIdentifier);
  return font?.sizes ?? [];
}

/** Derive content type from line text: numbers-only, letters-only, or null (empty / any). */
export function getContentType(text: string): 'numbers' | 'letters' | null {
  const trimmed = (text || '').trim();
  if (trimmed.length === 0) return null;
  const hasDigit = /\d/.test(trimmed);
  const hasLetter = /[a-zA-Z]/.test(trimmed);
  if (hasLetter) return 'letters';
  if (hasDigit) return 'numbers';
  return null;
}

/** Filter sizes by content type: numbers -> numberSizeOnly, letters/null -> !numberSizeOnly. */
export function filterSizesByContentType(
  sizes: FontSizeOption[],
  contentType: 'numbers' | 'letters' | null
): FontSizeOption[] {
  if (contentType === 'numbers') return sizes.filter((s) => s.numberSizeOnly);
  return sizes.filter((s) => !s.numberSizeOnly);
}

/**
 * Font size valid for the given font and line text; otherwise first allowed size (matches SelectedLineSettings).
 */
export function resolveLineFontSizeForFont(
  line: { text?: string; fontSize?: string },
  fontData: FontDataEntry[],
  fontIdentifier: string
): string {
  const allSizes = getSizesForFont(fontData, fontIdentifier);
  const available = filterSizesByContentType(allSizes, getContentType(line.text ?? ''));
  const current = line.fontSize ?? '';
  if (available.length === 0) return current;
  if (available.some((s) => s.value === current)) return current;
  return available[0].value;
}

export function getSpecialCharactersForFont(
  data: FontDataEntry[],
  fontIdentifier: string
): string {
  const font = getFontByIdentifier(data, fontIdentifier);
  return font?.specialCharacters ?? '';
}

export function getCrossingSizesForFont(
  data: FontDataEntry[],
  fontIdentifier: string
): CrossingSize[] {
  const font = getFontByIdentifier(data, fontIdentifier);
  return font?.crossingSizes ?? [];
}

const FONT_TYPE_ORDER = [FONT_TYPE_LETTERING, FONT_TYPE_SINGLE_LETTERS];

/** Font-level execution types when size has no fontTypes array (legacy API). */
export function getFontLevelFontTypes(font: FontDataEntry | undefined): string[] {
  const types: string[] = [];
  if (font?.lettering) types.push(FONT_TYPE_LETTERING);
  if (font?.individualLetters) types.push(FONT_TYPE_SINGLE_LETTERS);
  return types;
}

/** Execution types for a single size; falls back to font-level lettering/individualLetters flags. */
export function getFontTypesForSize(
  size: FontSizeOption | undefined,
  font: FontDataEntry | undefined
): string[] {
  if (size?.fontTypes != null && size.fontTypes.length > 0) {
    return size.fontTypes;
  }
  return getFontLevelFontTypes(font);
}

/** Intersection of fontTypes across all lines' current sizes; no lines → font-level flags. */
export function getCommonFontTypesForLines(
  fontData: FontDataEntry[],
  fontIdentifier: string,
  lines: { fontSize?: string }[]
): string[] {
  const font = getFontByIdentifier(fontData, fontIdentifier);
  if (!font) return [];

  if (lines.length === 0) {
    return getFontLevelFontTypes(font);
  }

  const sizes = font.sizes ?? [];
  const seenSizeValues = new Set<string>();
  const typeSets: string[][] = [];

  for (const line of lines) {
    const sizeValue = line.fontSize ?? '';
    if (seenSizeValues.has(sizeValue)) continue;
    seenSizeValues.add(sizeValue);
    const sizeEntry = sizes.find((s) => s.value === sizeValue);
    typeSets.push(getFontTypesForSize(sizeEntry, font));
  }

  if (typeSets.length === 0) {
    return getFontLevelFontTypes(font);
  }

  let intersection = new Set(typeSets[0]);
  for (let i = 1; i < typeSets.length; i++) {
    const allowed = new Set(typeSets[i]);
    intersection = new Set([...intersection].filter((t) => allowed.has(t)));
  }

  return FONT_TYPE_ORDER.filter((t) => intersection.has(t));
}

/** Keep current font type if available; otherwise first in canonical order. */
export function resolveFontType(current: string, available: string[]): string {
  if (available.length === 0) return current;
  if (available.includes(current)) return current;
  return available[0];
}